import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  getUserAsync,
  loginAsync,
  loginWithCodeAsync
} from '../state/auth/authSlice';
import { AppDispatch, RootState } from '../state/store';
import { LoginUserData } from '../types/interfaces';

import '../ui/css/LoginPage.css';

const LoginPage = () => {
  const isAuth = useSelector((state: RootState) => state.auth.isAuth);
  const twoFactorEnabled = useSelector(
    (state: RootState) => state.auth.twoFactorEnabled
  );
  const errorMessage = useSelector(
    (state: RootState) => state.auth.errorMessage
  );
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const hasError = useMemo(() => errorMessage !== '', [errorMessage]);

  useEffect(() => {
    const checkFirstLogin = async () => {
      if (isAuth) {
        // Получаем информацию о пользователе
        const result = await dispatch(getUserAsync());
        const userInfo = result.payload as { isFirstLogin?: boolean };
        
        if (userInfo?.isFirstLogin) {
          // Сохраняем данные логина для последующей авторизации после установки пароля
          if (values.current) {
            sessionStorage.setItem('pendingLoginData', JSON.stringify(values.current));
          }
          navigate('/set-password');
        } else {
          navigate('/');
        }
      }
    };
    checkFirstLogin();
  }, [isAuth, dispatch, navigate]);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const values = useRef<LoginUserData | null>({
    username: '',
    password: '',
    code: ''
  });

  const handleChange =
    (name: string) => (event: { target: { value: string } }) => {
      values.current = { ...values.current, [name]: event.target.value };
    };

  const handleSubmit = useCallback(
    async (event: { preventDefault: () => void }) => {
      event.preventDefault();
      const username = values.current?.username;
      const password = values.current?.password;
      const code = values.current?.code;

      if (twoFactorEnabled && username && password && code)
        await dispatch(loginWithCodeAsync({ username, password, code }));
      else if (username && password)
        await dispatch(loginAsync({ username, password }));
    },
    [dispatch, twoFactorEnabled]
  );

  useEffect(() => {
    const keyDownHandler = async (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        await handleSubmit(event);
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [handleSubmit]);

  return (
    <div className="login-page wrapper wrapper--login-page">
      <img 
        src="/Delo-Trak_Logo_Color_1.png" 
        alt="Дело.Трек" 
        style={{ height: 'auto', maxWidth: '300px', marginBottom: '20px' }}
      />
      <div className="description">
        Автоматизация процессов управления задачами полевого персонала
      </div>
      <form className="autorization-form" onSubmit={handleSubmit}>
        <div className="autorization-form__message">Требуется вход</div>
        <Divider />
        <div className="flex flex-column md:flex-row gap-3 pb-0">
          {!twoFactorEnabled ? (
            <div className="emploee-card__column  flex flex-column w-full">
              <div className="flex gap-3 justify-content-between card-row--bottom-margin ">
                <div style={{ width: '100%' }}>
                  <InputText
                    key={'username'}
                    placeholder="Имя пользователя"
                    className={
                      'login-page__input w-full' +
                      (hasError ? ' error-color' : '')
                    }
                    onChange={handleChange('username')}
                    required
                  />
                </div>
              </div>

              <div className="flex flex-column">
                <div className="flex justify-content-between align-items-center gap-3">
                  <InputText
                    key={'password'}
                    placeholder="Пароль"
                    className={
                      'login-page__input w-full' +
                      (hasError ? ' error-color' : '')
                    }
                    onChange={handleChange('password')}
                    type={isPasswordVisible ? 'text' : 'password'}
                    required
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="emploee-card__column  flex flex-column w-full">
              <div className="flex gap-3 justify-content-between card-row--bottom-margin ">
                <div style={{ width: '100%' }}>
                  <InputText
                    key={'code'}
                    placeholder="Введите временный код"
                    className={
                      'login-page__input w-full' +
                      (hasError ? ' error-color' : '')
                    }
                    onChange={handleChange('code')}
                    required
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {!twoFactorEnabled && (
          <div className="flex flex-column password" style={{ height: '50px' }}>
            <Button
              className="p-button-text show-password-button"
              icon={isPasswordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'}
              onClick={(e) => {
                e.preventDefault();
                setIsPasswordVisible(!isPasswordVisible);
              }}
            />
          </div>
        )}
        <Button label="Вход" className="w-full" />
        {errorMessage && errorMessage !== '' && (
          <div className="flex flex-column error-message">{errorMessage}</div>
        )}
      </form>
      <img 
        src="/DevelopedBy.png" 
        alt="разработано компанией Ростелеком" 
        className="mt-5"
        style={{ height: '30px', maxWidth: '300px', marginBottom: '20px' }}
      />
    </div>
  );
};
export default LoginPage;
